/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { getAccessTokenForAPI } from './auth.service';
import { showMessage } from './notification.service';
import { trackException } from './app-insights.service';

/**
 * Base API service for making authenticated calls to the backend
 */

/**
 * Strip query string + protocol+host from an endpoint so telemetry only
 * sees the route portion. URLs in this app contain GUIDs in the path
 * (clientId, documentId, etc.) which is fine to send, but query strings
 * may carry user-provided filters we'd rather not log.
 */
function pathOnly(endpoint: string): string {
  try {
    return new URL(endpoint).pathname;
  } catch {
    return endpoint.split('?')[0];
  }
}

/**
 * Mark an error as already reported to App Insights so the outer catch
 * doesn't re-track it. Set on Errors thrown from the inner non-OK path.
 */
// `status` carries the HTTP status code so callers don't have to
// string-match it back out of the message. getUserFriendlyErrorMessage
// reads it directly, which is both more reliable and safer than
// scanning the message — the message embeds the response body, so a
// body that happens to contain "413" or "404" would otherwise be
// misread as that status.
type TrackedError = Error & { _telemetryTracked?: boolean; status?: number };

/**
 * Generic function to call backend API endpoints
 * @param endpoint - The API endpoint URL (full URL)
 * @param method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param body - Request body for POST/PUT requests
 * @returns Promise with the API response
 */
export async function callBackendAPI<T = any>(endpoint: string, method: string = 'GET', body?: any): Promise<T> {
  try {
    const accessToken = await getAccessTokenForAPI();

    const requestOptions: RequestInit = {
      method: method,
      headers: {
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json'
      }
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      requestOptions.body = JSON.stringify(body);
    }

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Backend API Error:', response.status, response.statusText, errorBody);
      const errorMessage = `Backend API error: ${response.status} ${response.statusText}`;
      showMessage(errorMessage);
      const err: TrackedError = new Error(`${errorMessage} - ${errorBody}`);
      err.status = response.status;
      // Track here (with status) rather than in the outer catch so the
      // event has the response status code attached. Sentinel flag stops
      // the outer catch double-firing on the rethrow.
      trackException(err, {
        api_endpoint: pathOnly(endpoint),
        api_method: method,
        api_status: response.status,
      });
      err._telemetryTracked = true;
      throw err;
    }

    // Handle 204 No Content responses (no body to parse)
    if (response.status === 204) {
      return undefined as T;
    }

    return await response.json();
  } catch (error) {
    console.error('Error calling backend API:', error);
    const err = error as TrackedError;
    if (!err._telemetryTracked) {
      // Network failure / auth failure / JSON parse — no HTTP status to report.
      showMessage(`Failed to call backend API: ${(error as Error).message}`);
      trackException(err instanceof Error ? err : new Error(String(error)), {
        api_endpoint: pathOnly(endpoint),
        api_method: method,
      });
    }
    throw error;
  }
}

/**
 * Function to GET data from backend API endpoint
 * @param endpoint - The API endpoint URL (full URL)
 * @returns Promise with the API response
 */
export async function getFromBackendAPI<T = any>(endpoint: string): Promise<T> {
  return await callBackendAPI<T>(endpoint, 'GET');
}

/**
 * Function to POST data to backend API endpoint
 * @param endpoint - The API endpoint URL (full URL)
 * @param data - Data to post
 * @returns Promise with the API response
 */
export async function postToBackendAPI<T = any>(endpoint: string, data: any): Promise<T> {
  return await callBackendAPI<T>(endpoint, 'POST', data);
}

/**
 * Function to PUT data to backend API endpoint
 * @param endpoint - The API endpoint URL (full URL)
 * @param data - Data to put
 * @returns Promise with the API response
 */
export async function putToBackendAPI<T = any>(endpoint: string, data: any): Promise<T> {
  return await callBackendAPI<T>(endpoint, 'PUT', data);
}

/**
 * Function to PATCH data to backend API endpoint
 * @param endpoint - The API endpoint URL (full URL)
 * @param data - Data to patch
 * @returns Promise with the API response
 */
export async function patchToBackendAPI<T = any>(endpoint: string, data: any): Promise<T> {
  return await callBackendAPI<T>(endpoint, 'PATCH', data);
}

/**
 * Function to DELETE from backend API endpoint
 * @param endpoint - The API endpoint URL (full URL)
 * @returns Promise with the API response
 */
export async function deleteFromBackendAPI<T = any>(endpoint: string): Promise<T> {
  return await callBackendAPI<T>(endpoint, 'DELETE');
}

/**
 * Function to upload a file to backend API endpoint using FormData
 * @param endpoint - The API endpoint URL (full URL)
 * @param formData - FormData containing the file and any additional fields
 * @returns Promise with the API response
 */
export async function uploadFileToBackendAPI<T = any>(endpoint: string, formData: FormData): Promise<T> {
  try {
    const accessToken = await getAccessTokenForAPI();

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + accessToken,
        // Don't set Content-Type - browser will set it with correct multipart boundary
      },
      body: formData
    };

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Backend API Error:', response.status, response.statusText, errorBody);
      const errorMessage = `Backend API error: ${response.status} ${response.statusText}`;
      showMessage(errorMessage);
      const err: TrackedError = new Error(`${errorMessage} - ${errorBody}`);
      err.status = response.status;
      trackException(err, {
        api_endpoint: pathOnly(endpoint),
        api_method: 'POST',
        api_status: response.status,
        api_upload: true,
      });
      err._telemetryTracked = true;
      throw err;
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading file to backend API:', error);
    const err = error as TrackedError;
    if (!err._telemetryTracked) {
      showMessage(`Failed to upload file: ${(error as Error).message}`);
      trackException(err instanceof Error ? err : new Error(String(error)), {
        api_endpoint: pathOnly(endpoint),
        api_method: 'POST',
        api_upload: true,
      });
    }
    throw error;
  }
}

/**
 * Function to download a file from backend API endpoint
 * @param endpoint - The API endpoint URL (full URL)
 * @returns Promise with the file Blob
 */
export async function downloadFileFromBackendAPI(endpoint: string): Promise<Blob> {
  try {
    const accessToken = await getAccessTokenForAPI();

    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + accessToken,
      }
    };

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Backend API Error:', response.status, response.statusText, errorBody);
      const errorMessage = `Backend API error: ${response.status} ${response.statusText}`;
      showMessage(errorMessage);
      const err: TrackedError = new Error(`${errorMessage} - ${errorBody}`);
      err.status = response.status;
      trackException(err, {
        api_endpoint: pathOnly(endpoint),
        api_method: 'GET',
        api_status: response.status,
        api_download: true,
      });
      err._telemetryTracked = true;
      throw err;
    }

    return await response.blob();
  } catch (error) {
    console.error('Error downloading file from backend API:', error);
    const err = error as TrackedError;
    if (!err._telemetryTracked) {
      showMessage(`Failed to download file: ${(error as Error).message}`);
      trackException(err instanceof Error ? err : new Error(String(error)), {
        api_endpoint: pathOnly(endpoint),
        api_method: 'GET',
        api_download: true,
      });
    }
    throw error;
  }
}
